import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, MailCheck, ArrowLeft, ArrowRight, Loader2, Inbox } from "lucide-react";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            setSent(true);
            if (data.dev_reset_url) {
                toast.message("Dev reset link", { description: data.dev_reset_url });
            }
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mx-auto flex min-h-[80vh] max-w-2xl items-center px-4 py-12 sm:px-6">
            <Card
                data-testid="forgot-password-card"
                className="noise-overlay relative w-full overflow-hidden border-border bg-card p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-12"
            >
                <div className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-40 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(240,180,41,0.16)_0%,rgba(240,180,41,0)_70%)]" />

                {sent ? (
                    <div className="relative z-10 flex flex-col items-center text-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brass/15 text-brass ring-1 ring-brass/30">
                            <MailCheck className="h-8 w-8" aria-hidden />
                        </span>
                        <h1 data-testid="forgot-sent-title" className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                            Check your email
                        </h1>
                        <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                            If an account exists for{" "}
                            <span data-testid="forgot-sent-address" className="font-semibold text-foreground">{email}</span>,
                            we&rsquo;ve sent a link to reset your password. The link expires in 60 minutes.
                        </p>

                        <div className="mt-8 flex w-full max-w-sm flex-col gap-3">
                            <Button
                                data-testid="forgot-back-to-login-btn"
                                size="lg"
                                className="w-full rounded-[10px] font-semibold"
                                onClick={() => navigate("/login")}
                            >
                                Back to login <ArrowRight className="ml-1.5 h-4 w-4" />
                            </Button>
                            <Button
                                data-testid="forgot-try-again-btn"
                                size="lg"
                                variant="outline"
                                className="w-full rounded-[10px] border-electric/40 text-foreground hover:bg-electric/10"
                                onClick={() => setSent(false)}
                            >
                                Use a different email
                            </Button>
                        </div>

                        <p className="mt-8 flex items-start gap-2.5 rounded-xl border border-border bg-secondary/30 p-4 text-left text-xs leading-relaxed text-muted-foreground">
                            <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-electric" />
                            <span>Can&rsquo;t find it? Check your <span className="font-medium text-foreground">spam</span> or <span className="font-medium text-foreground">promotions</span> folder &mdash; it can take a minute to arrive.</span>
                        </p>
                    </div>
                ) : (
                    <div className="relative z-10">
                        <div className="flex flex-col items-center text-center">
                            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brass/15 text-brass ring-1 ring-brass/30">
                                <KeyRound className="h-8 w-8" aria-hidden />
                            </span>
                            <h1 data-testid="forgot-password-title" className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                                Forgot your password?
                            </h1>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                                Enter the email associated with your account and we&rsquo;ll send you a secure link to reset your password.
                            </p>
                        </div>

                        <form onSubmit={submit} className="mx-auto mt-8 w-full max-w-sm space-y-5">
                            <div className="space-y-2.5">
                                <Label htmlFor="email">Email address</Label>
                                <Input
                                    id="email"
                                    data-testid="forgot-email-input"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                size="lg"
                                disabled={loading}
                                data-testid="forgot-submit-btn"
                                className="w-full rounded-[10px] font-semibold"
                            >
                                {loading ? (
                                    <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Sending&hellip;</>
                                ) : (
                                    <>Send reset link <ArrowRight className="ml-1.5 h-4 w-4" /></>
                                )}
                            </Button>
                        </form>

                        <p className="mt-8 text-center text-sm text-muted-foreground">
                            <Link to="/login" data-testid="forgot-back-link" className="inline-flex items-center gap-1.5 font-medium text-electric hover:underline">
                                <ArrowLeft className="h-3.5 w-3.5" /> Back to login
                            </Link>
                        </p>
                    </div>
                )}
            </Card>
        </div>
    );
}
